// =============================================================================
//  CSV export helper (client-side)
//
//  Used by v3 list pages where the backend doesn't expose a bulk CSV
//  endpoint (Clients, Mandates).  Builds a CSV from the in-memory list
//  data and triggers a browser download via an injected anchor — same
//  technique the existing download sagas (tombstone.saga,
//  employee.saga) use for server-side exports, so the user-facing UX
//  is identical.
//
//  Limitations vs. a server-side export:
//    - Only the fields the frontend has loaded.  Deep relationships
//      not eagerly fetched by the list endpoint won't appear.
//    - No XLSX, only CSV.  Adequate for the v3 pilot.
//
//  When the backend grows /clients.csv and /mandates.csv we should
//  swap these call-sites over to a real download saga and delete this
//  helper.
// =============================================================================

type Cell = string | number | boolean | null | undefined | Date;

export interface CsvColumn<T> {
  header: string;
  /** Pull the cell value out of a row.  Return undefined / null for blank. */
  accessor: (row: T) => Cell;
}

const escapeCell = (value: Cell): string => {
  if (value === null || value === undefined) return '';
  let str: string;
  if (value instanceof Date) {
    // ISO date-only is more useful in spreadsheets than full timestamps.
    str = value.toISOString().slice(0, 10);
  } else {
    str = String(value);
  }
  // RFC 4180: wrap in quotes when the value contains a comma, quote, or
  // newline; double up any embedded quotes.
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCsv = <T>(rows: T[], columns: ReadonlyArray<CsvColumn<T>>): string => {
  const header = columns.map((c) => escapeCell(c.header)).join(',');
  const body = rows
    .map((row) => columns.map((c) => escapeCell(c.accessor(row))).join(','))
    .join('\r\n');
  return body ? `${header}\r\n${body}\r\n` : `${header}\r\n`;
};

const triggerDownload = (csv: string, filename: string) => {
  // BOM so Excel auto-detects UTF-8 when the file contains characters
  // outside ASCII (Zandile/é/etc.).
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const anchor = document.createElement('a');
  anchor.href = window.URL.createObjectURL(blob);
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Release the object URL on the next tick so the click has time to
  // resolve in all browsers.
  setTimeout(() => window.URL.revokeObjectURL(anchor.href), 1_000);
};

/**
 * Build + download a CSV from `rows` using `columns`.
 *
 * `filenameBase` is the human-readable prefix (e.g. "Clients"); we append
 * a YYYYMMDDHHMM stamp to match the convention used by the server-side
 * exports (WriteXLSX uses the same format).
 */
export const exportCsv = <T>(
  rows: T[],
  columns: ReadonlyArray<CsvColumn<T>>,
  filenameBase: string
): void => {
  const csv = buildCsv(rows, columns);
  const stamp = new Date()
    .toISOString()
    .replace(/[-:T]/g, '')
    .slice(0, 12); // 2026-05-15T13:42:01Z → "202605151342"
  triggerDownload(csv, `${filenameBase}-${stamp}.csv`);
};
